import { z } from 'zod';
import {
    DesktopAggregateTargetSchema,
    DesktopCollectionTargetSchema,
    DesktopFocusPolicySchema,
    DesktopUiEffectReasonSchema,
    type DesktopAggregateTarget,
    type DesktopCollectionTarget,
    type DesktopUiEffectReason,
} from '../dtos/chatbot/desktop-ui';

export const REALTIME_NAMESPACE = '/realtime' as const;

export const REALTIME_SOCKET_EVENTS = {
    message: 'realtime:event',
    tokenRefreshed: 'auth:token-refreshed',
    channelSubscribe: 'realtime:channel:subscribe',
    channelUnsubscribe: 'realtime:channel:unsubscribe',
} as const;

export const REALTIME_CHANNELS = {
    profileMatchings: 'profile-matchings',
    notifications: 'notifications',
    outbound: 'outbound',
    userProfile: 'user-profile',
    desktopInvalidations: 'desktop-invalidations',
} as const;

export const REALTIME_WORKSPACE_CHANNELS = {
    desktopInvalidations: REALTIME_CHANNELS.desktopInvalidations,
} as const;

export type RealtimeChannel = (typeof REALTIME_CHANNELS)[keyof typeof REALTIME_CHANNELS] | (string & {});
export type RealtimeWorkspaceChannel = (typeof REALTIME_WORKSPACE_CHANNELS)[keyof typeof REALTIME_WORKSPACE_CHANNELS];

export function isWorkspaceRealtimeChannel(channel: string): channel is RealtimeWorkspaceChannel {
    return Object.values(REALTIME_WORKSPACE_CHANNELS).includes(channel as RealtimeWorkspaceChannel);
}

export interface RealtimeEnvelope<TPayload = unknown, TChannel extends RealtimeChannel = RealtimeChannel, TTopic extends string = string> {
    channel: TChannel;
    topic: TTopic;
    payload: TPayload;
    correlationId?: string;
    entityId?: string;
}

export const PROFILE_MATCHINGS_REALTIME_TOPICS = {
    progress: 'progress',
} as const;

export const NOTIFICATIONS_REALTIME_TOPICS = {
    created: 'created',
    read: 'read',
    unread: 'unread',
} as const;

export const OUTBOUND_REALTIME_TOPICS = {
    importProgress: 'import-progress',
} as const;

export const USER_PROFILE_REALTIME_TOPICS = {
    invalidated: 'invalidated',
} as const;

export const DESKTOP_INVALIDATION_REALTIME_TOPICS = {
    invalidate: 'invalidate',
} as const;

export const WorkspaceDesktopInvalidationTargetSchema = z.discriminatedUnion('kind', [
    DesktopCollectionTargetSchema,
    DesktopAggregateTargetSchema,
]);
export type WorkspaceDesktopInvalidationTarget = DesktopCollectionTarget | DesktopAggregateTarget;

export const WorkspaceDesktopInvalidateEffectSchema = z.object({
    kind: z.literal('invalidate'),
    targets: z.array(WorkspaceDesktopInvalidationTargetSchema).min(1),
    focusPolicy: DesktopFocusPolicySchema,
    reason: DesktopUiEffectReasonSchema,
});
export type WorkspaceDesktopInvalidateEffect = {
    kind: 'invalidate';
    targets: WorkspaceDesktopInvalidationTarget[];
    focusPolicy: z.infer<typeof DesktopFocusPolicySchema>;
    reason: DesktopUiEffectReason;
};

export type DesktopInvalidationRealtimeEnvelope = RealtimeEnvelope<
    WorkspaceDesktopInvalidateEffect,
    typeof REALTIME_CHANNELS.desktopInvalidations,
    typeof DESKTOP_INVALIDATION_REALTIME_TOPICS.invalidate
>;

export type RealtimeChannelSubscriptionRequest = {
    channel: RealtimeChannel;
};

export type RealtimeChannelSubscriptionAck = { ok: true } | { ok: false; code: 'UNAUTHORIZED' | 'CHANNEL_FORBIDDEN' | 'CHANNEL_UNKNOWN' };

export type ProfileMatchingProgressPayload = {
    type: 'phase' | 'candidates_ready' | 'batch_result' | 'done' | 'error';
    data?: unknown;
};

export type OutboundImportProgressPayload = {
    type: 'row_result' | 'done' | 'error';
    data?: unknown;
};

export type ImportProgressPayload = OutboundImportProgressPayload;

export type ProfileMatchingProgressEnvelope = RealtimeEnvelope<
    ProfileMatchingProgressPayload,
    typeof REALTIME_CHANNELS.profileMatchings,
    typeof PROFILE_MATCHINGS_REALTIME_TOPICS.progress
>;

export type OutboundImportProgressEnvelope = RealtimeEnvelope<
    OutboundImportProgressPayload,
    typeof REALTIME_CHANNELS.outbound,
    typeof OUTBOUND_REALTIME_TOPICS.importProgress
>;

export type FrontNotificationPayload = {
    recipientId: string;
    notificationId: string;
    channel: 'front';
    type: string;
    source: string;
    title: string;
    message: string;
    createdAt: string;
    expiresAt: string;
    readAt: string | null;
    isRead: boolean;
    actionUrl?: string | null;
    metadata?: Record<string, unknown> | null;
};

export type UserProfileInvalidatedPayload = {
    reason: 'users-group-changed';
    usersGroupId?: string | null;
    group: Array<Record<string, unknown>>;
};

export type UserProfileInvalidatedEnvelope = RealtimeEnvelope<
    UserProfileInvalidatedPayload,
    typeof REALTIME_CHANNELS.userProfile,
    typeof USER_PROFILE_REALTIME_TOPICS.invalidated
>;

export type NotificationsRealtimeTopic = (typeof NOTIFICATIONS_REALTIME_TOPICS)[keyof typeof NOTIFICATIONS_REALTIME_TOPICS];

export type FrontNotificationEnvelope<TTopic extends NotificationsRealtimeTopic = NotificationsRealtimeTopic> = RealtimeEnvelope<
    FrontNotificationPayload,
    typeof REALTIME_CHANNELS.notifications,
    TTopic
>;

export * from './erp-presence';
export * from './erp-desktop-sync';
